import { ChangeEvent, FC, useEffect, useRef, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import ExternalLink from "../../../assets/images/clinical/ExternalLink.svg"
import {
  FormAutoComplete,
  FormInput,
  UButton,
  UModalPopup,
  UText,
} from "../../../components"
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
} from "../../../components/mui.components"
import NavigationBlockPopup from "../../../components/NavigationBlockPopup/NavigationBlockPopup"
import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { setAlert } from "../../../core/app/slices/alert/alertSlice"
import { resetCasedata } from "../../../core/app/slices/case/caseSlice"
import {
  getCaseByPatient,
  updateCaseById,
} from "../../../core/app/slices/case/caseThunkApi"
import {
  resetAction,
  resetCase,
} from "../../../core/app/slices/clinical/clinicalSlice"
import { setIsRouteChangeBlocked } from "../../../core/app/slices/navigationPopup/navigationPopupSlice"
import {
  addNewPatient,
  fetchExistingPatientList,
  fetchPatientById,
  updatePatientData,
  addPatientV2,
  addCaseV2,
} from "../../../core/app/slices/patients"
import {
  AddNewCaseParams,
  AddNewPatientParams,
  updatePatientParams,
} from "../../../core/app/slices/patients/patient.types"
import {
  setAssignedTo,
  setData,
  setOpenExistingPatientCheckModal,
} from "../../../core/app/slices/patients/patientSlice"
import { resetPhotoLists } from "../../../core/app/slices/records/photograph/photographSlice"
import { resetXrays } from "../../../core/app/slices/records/xrays/xraysSlice"
import { updateUserPreferences } from "../../../core/app/slices/user/userApis"
import { updateCurrentOrgId } from "../../../core/app/slices/user/userSlice"
import { RootState } from "../../../core/app/store"
import store from "../../../core/app/store"
import {
  IExistingPatientList,
  IPatientForm,
} from "../../../core/model/interface/IPatient"
import { validations } from "../../../core/utils/validations"
import { useCallbackPrompt } from "../../../hooks/useCallbackPrompt"
import { useMyNavigation } from "../../../hooks/useMyNavigation"
import { CENETER_COLUMN_ALIGN } from "../../../theme/theme.util"
import ScannerPatients from "../ScannerPatients/ScannerPatients"

import usePermissions from "@/hooks/userPermission"
import { UI_PERMISSION } from "@/core/utils/access/permissionConstants"
import CaseActionPopup from "../Overview/components/CaseActionPopup"
import ExistingPatientCheckModal from "../Overview/components/existingPatientCheckModal"
import { getCaseDetail } from "@/core/global/ThirdDataMgr"
import useLocalStorageListener from "@/modules/Settings/Integrations/localStorageHooks"
import { getCaseByUuid } from "@/core/global/httpMedit"
import { getBirthday, getCaseComment } from "@/core/global/http3Shape"
import { getPatientslist } from "@/core/global/httpAlliedstar"

const PatientForm: FC = () => {
  const { hasAny } = usePermissions()
  const navigate = useMyNavigation()
  const dispatch = useAppDispatch()
  const [isShowCancel, setIsShowCancel] = useState(false)
  const [existingPatientList, setExistingPatientList] = useState<
    IExistingPatientList[]
  >([])
  const { patientId, orgId } = useParams()
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)

  const { doctorList } = useAppSelector(
    (state: RootState) => state.doctorService,
  )
  const [isAlertOpen, setIsAlertOpen] = useState<"addNewCase" | "">("")
  const regex = /^[^\/\\:*?<>\|$'".,;]*$/
  const {
    newPatientAPI,
    patientData,
    caseId,
    existingPatients,
    assignedTo,
    caseDetailForShape,
    isExistingPatientCheckModal,
    scannerSelect,
  } = useAppSelector((state: RootState) => state.patientService)
  console.log("990", caseDetailForShape)
  const { caseList } = useAppSelector((state: RootState) => state.caseService)
  const { preferences } = useAppSelector(
    (state: RootState) => state.userService.user,
  )
  const { t } = useTranslation("common")
  const [blurTimeout, setBlurTimeout] = useState(null)

  const {
    control,
    formState: { errors },
    getFieldState,
    trigger,
    setValue,
    watch,
    handleSubmit,
    getValues,
  } = useForm<IPatientForm>({ mode: "onSubmit" })
  const formValues = watch()
  const dayRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)
  const [existingPatient, setExistingPatient] = useState(null)
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    Object.keys(formValues).some((key) => !!getValues()[key]) &&
      !formSubmitted &&
      !isShowCancel &&
      !isExistingPatientCheckModal,
  )
  const checkIfValueHaveChanged = () => {
    const data = getValues()
    if (
      data.firstName !== patientData.firstName ||
      data.lastName !== patientData.lastName ||
      (data.notes !== patientData.notes &&
        !(data.notes === "" && patientData.notes === null)) ||
      patientData.dob !== data.year + "-" + data.month + "-" + data.day ||
      data.doctor !== parseInt(caseList[0].assigned_to)
    )
      return true
  }

  const onSubmit: SubmitHandler<IPatientForm> = async (data) => {
    clearTimeout(blurTimeout)
    setFormSubmitted(true)
    dispatch(setAssignedTo(data.doctor))
    if (patientId) {
      if (checkIfValueHaveChanged()) {
        const payload: updatePatientParams = {
          first_name: String(data.firstName).trim(),
          last_name: String(data.lastName).trim(),
          patient_dob: data.year + "-" + data.month + "-" + data.day,
          ref_notes: data.notes,
        }
        await dispatch(
          updatePatientData({
            patientId,
            payload,
          }),
        )
        if (data.doctor !== parseInt(caseList[0].assigned_to)) {
          await dispatch(
            updateCaseById({
              patientId: patientId,
              caseId: "" + caseList[0].id,
              payload: {
                assigned_to: data.doctor,
              },
            }),
          )
        }
        // go to choose case type
        navigate(
          `/patients/choosetype/patient/${patientData.id}/case/${caseList[0].id}`,
        )
        // navigate(
        //   `/records/patient/${patientData.id}/case/${caseList[0].id}/scans`,
        // )
      } else {
        navigate(
          `/patients/choosetype/patient/${patientData.id}/case/${caseList[0].id}`,
        )
        // navigate(
        //   `/records/patient/${patientData.id}/case/${caseList[0].id}/scans`,
        // )
      }
    } else {
      const assigned_to = data.doctor
      const payload: AddNewPatientParams = {
        firstname: String(data.firstName).trim(),
        lastname: String(data.lastName).trim(),
        dob: data.year + "-" + data.month + "-" + data.day,
        assigned_to,
        notes: data.notes,
      }
      // v1
      dispatch(addNewPatient(payload))
      // v2
      // dispatch(addPatientV2(payload)).then((res) => {
      //   const { data } = res.payload
      //   const { msg } = data
      //   if (msg.toUpperCase() === "SUCCESS") {
      //     const patient_id = data.data.id
      //     const payloadCase: AddNewCaseParams = {
      //       patient_id,
      //       assign_to: assigned_to,
      //     }
      //     // create case v2
      //     dispatch(addCaseV2(payloadCase)).then((res) => {
      //       const { data } = res.payload
      //       const { msg } = data
      //       if (msg.toUpperCase() === "SUCCESS") {
      //         const case_id = data.data.id
      //         // navigate(`/clinical/patient/${patient_id}/case/${case_id}/treat`)
      //         navigate(
      //           `/patients/choosetype/patient/${patient_id}/case/${case_id}`,
      //         )
      //         dispatch(
      //           setAlert({
      //             message: t("newpatient.createSuccess"),
      //           }),
      //         )
      //       }
      //     })
      //   }
      // })
    }
  }
  const validateDate = (day: string) => {
    const month =
      getFieldState("month").isDirty && !getFieldState("month").invalid
        ? parseInt(watch("month"), 10)
        : new Date().getMonth()
    const year =
      getFieldState("year").isDirty && !getFieldState("year").invalid
        ? parseInt(watch("year"), 10)
        : 0
    const date = new Date(year, month - 1, parseInt(day, 10))
    return date.getMonth() === month - 1 && !(date > new Date())
  }
  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatientById({ patientId }))
      dispatch(getCaseByPatient({ patientId }))
    }
  }, [patientId])
  useEffect(() => {
    if (!patientId && assignedTo) {
      setValue(
        "doctor",
        parseInt(
          doctorList.find((doctor) => parseInt(doctor.id) === assignedTo)?.id,
        ),
      )
    }
    if (!patientId) return
    if (patientData.id) {
      setValue("firstName", patientData?.firstName || "")
      setValue("lastName", patientData?.lastName || "")
      setValue("month", patientData?.dob.split("-")[1] || "")
      setValue("day", patientData?.dob.split("-")[2] || "")
      setValue("year", patientData?.dob.split("-")[0] || "")
      setValue("notes", patientData?.notes || "")
    }
    if (caseList[0] && doctorList.length) {
      setValue(
        "doctor",
        parseInt(
          doctorList.find((doctor) => doctor.id === caseList[0].assigned_to)
            ?.id || "0",
        ),
      )
    }
  }, [patientData, caseList, doctorList])

  const handleOnDontShowCheck = (event: ChangeEvent<HTMLInputElement>) => {
    setDontShowAgain(!dontShowAgain)
    setIsShowCancel(false)
    dispatch(
      updateUserPreferences({
        on_show_new_patient_cancel_warning: event.target.checked,
      }),
    )
  }

  const CancelModalContent = () => {
    return (
      <>
        <UText variant={"body1"}>{t("newpatient.cancelModal.text")}</UText>
        <Grid sx={{ ml: "10px", mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={dontShowAgain}
                onChange={handleOnDontShowCheck}
              />
            }
            label={
              <UText
                variant={"body1"}
                sxProp={{ pt: 1, letterSpacing: "0.15px" }}
              >
                {t("newpatient.cancelModal.userPreference")}
              </UText>
            }
          />
        </Grid>
      </>
    )
  }

  const [showExisting, setShowExisting] = useState<boolean>(false)

  useEffect(() => {
    if (showExisting) {
      const existingPatientsList = patientId
        ? existingPatients.filter((patient) => patient.id === patientId)
        : existingPatients
      setExistingPatientList([...existingPatientsList])
    }
  }, [existingPatients])

  useEffect(() => {
    if (!orgId) return
    dispatch(updateCurrentOrgId(orgId))
  }, [dispatch, orgId])

  const checkForExistingPatient = () => {
    const data = getValues()
    const allKeyhaveValue = Object.keys(data).every(
      (key) => key === "doctor" || key === "notes" || data[key],
    )
    if (allKeyhaveValue) {
      const dob = data.year + "-" + data.month + "-" + data.day
      clearTimeout(blurTimeout)
      const timeoutId = setTimeout(() => {
        dispatch(
          fetchExistingPatientList({
            existingPatientQueryparams: {
              firstName: data.firstName,
              lastName: data.lastName,
              dob: dob,
            },
          }),
        )
      }, 200)
      setBlurTimeout(timeoutId)
      setShowExisting(true)
    } else if (!allKeyhaveValue && existingPatients.length !== 0) {
      setExistingPatientList([])
      setShowExisting(false)
    }
  }

  const handleDefaultDoctorData = () => {
    if (patientId && doctorList.length && caseList[0]?.assigned_to) {
      return doctorList.find((doctor) => doctor.id === caseList[0].assigned_to)
        ?.label
    } else if (assignedTo) {
      return doctorList.find((doctor) => parseInt(doctor.id) === assignedTo)
        ?.label
    } else {
      return ""
    }
  }

  useEffect(() => {
    if (newPatientAPI === "pending") {
      //TODO set loader to true
    } else if (newPatientAPI === "succeeded" && !isExistingPatientCheckModal) {
      dispatch(setData({ newPatientAPI: "idle" }))
      dispatch(
        setAlert({
          message: patientId
            ? t("newpatient.updateSuccess")
            : t("newpatient.createSuccess"),
        }),
      )
      dispatch(resetPhotoLists())
      dispatch(resetXrays())
      dispatch(resetCase())
      dispatch(resetCasedata())
      dispatch(resetAction())
      if (patientData && (caseId || caseList[0].id)) {
        navigate(
          `/patients/choosetype/patient/${patientData.id}/case/${
            caseId || caseList[0].id
          }`,
        )
        // navigate(
        //   `/records/patient/${patientData.id}/case/${
        //     caseId || caseList[0].id
        //   }/scans`,
        // )
      }
      //TODO should set loader to false
    }
  }, [newPatientAPI])

  const handleOnClickExistingPatient = (patientId) => {
    window.open(`/overview/patient/${patientId}`, "_blank")
  }

  useEffect(() => {
    if (!isShowCancel) return
    if (
      !Object.keys(getValues()).some((key) => !!getValues()[key]) ||
      (preferences && preferences?.on_show_new_patient_cancel_warning)
    )
      navigate("/patients")
  }, [isShowCancel])
  // clear cache data
  useEffect(() => {
    dispatch(setData({ caseListFor3Shape: [] }))
    return () => {
      dispatch(setAssignedTo(0))
      dispatch(setOpenExistingPatientCheckModal(false))
      setExistingPatient(null)
    }
  }, [])
  // scanner patients box
  const [showScannerPatients, setShowScannerPatients] = useState<boolean>(false)
  const choosePatient = (row) => {
    setValue("month", "")
    setValue("day", "")
    setValue("year", "")
    setValue("doctor", null)
    setValue("notes", "")
    setValue("firstName", row.Info.FirstName)
    setValue("lastName", row.Info.LastName)
    if (scannerSelect === "3Shape") {
      // stl and image
      const { Scans = [], Attachments = [], Info } = row
      const obj = {}
      Scans.map((i) => {
        if (i.FileType === "stl" && i.Href) {
          if (i.JawType === "upper") {
            const pre = i.Href.split("attachments")[0]
            obj.upper = `${pre}attachments/${i.Hash}`
          }
          if (i.JawType === "lower") {
            const pre = i.Href.split("attachments")[0]
            obj.lower = `${pre}attachments/${i.Hash}`
          }
        }
      })
      const imageTmp = []
      let birthHref = ""
      Attachments.map((i) => {
        if (["png", "jpg", "jpeg"].includes(i.FileType) && i.Href) {
          const obj = {}
          const pre = i.Href.split("attachments")[0]
          obj.href = `${pre}attachments/${i.Hash}`
          obj.name = i.Name
          obj.fileType = `image/${i.FileType}`
          imageTmp.push(obj)
        }
        if (i.FileType === "html") {
          birthHref = i.Href
        }
      })
      obj.Info = Info
      obj.Scans = Scans
      obj.Attachments = imageTmp
      dispatch(setData({ caseDetailForShape: obj }))
      // set birthday
      if (birthHref !== "") {
        getBirthday(birthHref).then((res) => {
          console.log("Birthday res", res)
          if (res) {
            const { year, month, day } = res
            setValue("month", month || "")
            setValue("day", day || "")
            setValue("year", year || "")
          }
        })
      }
      let comment = ""
      if (row.Info.Comments.length > 0) {
        row.Info.Comments.map((i) => {
          // getCaseComment(row.Info.CaseId, i.id).then((res) => {
          //   if (res) {
          //     // setValue("notes", res)
          //     comment += res + "\n"
          //   }
          // })
          comment += i.ShortComment + "\n"
        })
        setValue("notes", comment)
      }
    }
    if (scannerSelect === "Alliedstar") {
      const detail = JSON.parse(JSON.stringify(row))
      getCaseDetail({ type: "Alliedstar", id: row.Info.CaseId }).then((res) => {
        if (res && res.attachments) {
          const scanTmp = []
          const imageTmp = []
          res.attachments.map((i) => {
            if (i.fileFormat === "stl") {
              scanTmp.push(i)
            } else {
              imageTmp.push(i)
            }
          })
          detail.Attachments = imageTmp
          detail.Scans = scanTmp
          setValue("notes", res.comment)
          getPatientslist(res.patientName, res.patientId).then((res1) => {
            if (res1 && res1.data) {
              if(res1.data.data[0] && res1.data.data[0].birthDate) {
                const dob = res1.data.data[0].birthDate.split("T")[0].split("-")
                setValue("month", dob[1] || "")
                setValue("day", dob[2] || "")
                setValue("year", dob[0] || "")
              }
            }
          })
        }
        dispatch(setData({ caseDetailForShape: detail }))
      })
    }
    if (scannerSelect === "Medit") {
      const detail = JSON.parse(JSON.stringify(row))
      getCaseByUuid(row.Info.CaseId).then((res) => {
        if (res && res.data.files) {
          const scanTmp = []
          const imageTmp = []
          const keywords = ["up", "low", "mandibular", "maxillary", "arch"]
          res.data.files.map((i) => {
            if (keywords.some((keyword) => i.name.includes(keyword))) {
              scanTmp.push(i)
            } else {
              imageTmp.push(i)
            }
          })
          detail.Attachments = imageTmp
          detail.Scans = scanTmp
        }
        dispatch(setData({ caseDetailForShape: detail }))
      })
    }
  }
  const checkforActiveCase = () => {
    const payload = {
      firstname: patientData.firstName,
      lastname: patientData.lastName,
      dob: patientData.dob,
      patient_id: patientData.id,
      assigned_to: caseList[0]?.assigned_to || null,
    }
    dispatch(addNewPatient(payload)).then((res) => {
      dispatch(setData({ newPatientAPI: "idle" }))
      if (res.payload?.data?.status === "Success") {
        const case_id = res.payload.data.result?.case?.id
        navigate(
          `/patients/choosetype/patient/${patientData.id}/case/${case_id}`,
        )
      } else {
        setIsAlertOpen("addNewCase")
      }
    })
  }
  // locastorage key  3shapeData， meditData, alliesData
  const shapeLocal = useLocalStorageListener("3shapeData", null)
  const meditDataLocal = useLocalStorageListener("meditData", null)
  const alliesDataLocal = useLocalStorageListener("alliesData", null)
  const [scannerListData, setScannerListData] = useState([])
  // gei scanner list
  useEffect(() => {
    const tmp = []
    if (shapeLocal) {
      tmp.push({
        name: "3Shape",
        key: "3Shape",
      })
    }
    if (meditDataLocal) {
      tmp.push({
        name: "Medit",
        key: "Medit",
      })
    }
    if (alliesDataLocal) {
      tmp.push({
        name: "Alliedstar",
        key: "Alliedstar",
      })
    }
    setScannerListData(tmp)
  }, [shapeLocal, meditDataLocal, alliesDataLocal])
  return (
    <>
      <Grid
        container
        item
        sx={{ mt: 3, display: "flex", justifyContent: "center" }}
      >
        <Grid item xs={5} display={"flex"}>
          <UText variant={"h4"}>{t("newpatient.title")}</UText>
        </Grid>
        <Grid item xs={5} display={"flex"} justifyContent={"end"}>
          <UButton
            variant={"shade"}
            btnType={"button"}
            btnText={t("button.cancel")}
            onClickHandler={() => {
              setIsShowCancel(true)
            }}
            size={"small"}
          />
        </Grid>
      </Grid>
      <Grid item container justifyContent={"center"}>
        <Paper
          elevation={0}
          sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 5 }, borderRadius: 4 }}
        >
          {hasAny(UI_PERMISSION.UI_MODULE_SCANNER) &&
            scannerListData.length > 0 && (
              <>
                <UButton
                  sxProp={{ mt: 1, width: "100%", height: "36px" }}
                  variant={"contained"}
                  btnType={"text"}
                  btnText={`Import Patient From Scanner`}
                  onClickHandler={() => {
                    setShowScannerPatients(true)
                  }}
                />
                <UText variant={"caption"}>{t(t("newpatient.caption"))}</UText>
              </>
            )}
          <Box
            component={"form"}
            data-testid={"patient-form"}
            sx={{ ...CENETER_COLUMN_ALIGN, width: "100%", mt: 2 }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid container maxWidth={"sm"}>
              <UText variant={"h6"}>{t("formfields.name")}</UText>
              <Grid container rowSpacing={0} columnSpacing={1} marginBottom={2}>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formSxprops={{ my: 1 }}
                    inputLabel={"formfields.firstname"}
                    fieldName={"firstName"}
                    rules={{
                      required: "formfieldErrors.firstnamerequired",
                      pattern: {
                        value: regex,
                        message:
                          "The name can't contain any of the following characters: / \\ : * ? < > | $ ' \" , . ;",
                      },
                      maxLength: {
                        value: 30,
                        message: "formfieldErrors.namelengtherror",
                      },
                    }}
                    onBlurChange={() => {
                      if (watch("firstName").replace(/\s/g, "") === "") {
                        setValue("firstName", "")
                      }
                      checkForExistingPatient()
                    }}
                    errors={errors}
                    control={control}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formSxprops={{ my: 1 }}
                    inputLabel={"formfields.lastname"}
                    fieldName={"lastName"}
                    rules={{
                      required: "formfieldErrors.larstnamerequired",
                      pattern: {
                        value: regex,
                        message:
                          "The name can't contain any of the following characters: / \\ : * ? < > | $ ' \" , . ;",
                      },
                      maxLength: {
                        value: 30,
                        message: "formfieldErrors.namelengtherror",
                      },
                    }}
                    onBlurChange={() => {
                      if (watch("lastName").replace(/\s/g, "") === "") {
                        setValue("lastName", "")
                      }
                      checkForExistingPatient()
                    }}
                    errors={errors}
                    control={control}
                  />
                </Grid>
              </Grid>
              <UText variant={"h6"}>{t("formfields.dateofbirth")}</UText>
              <Grid container rowSpacing={0} columnSpacing={1} marginBottom={2}>
                <Grid item xs={4}>
                  <FormInput
                    formSxprops={{ my: 1 }}
                    inputLabel={"formfields.month"}
                    placeholder={"formfields.monthplaceholder"}
                    fieldName={"month"}
                    rules={{
                      required: "formfieldErrors.monthrequired",
                      pattern: validations.month.pattern,
                    }}
                    onBlurChange={() => {
                      trigger("month").then((valid) => {
                        if (valid) {
                          const fieldValue = watch("month")
                          fieldValue.length === 1
                            ? setValue("month", "0" + fieldValue)
                            : setValue("month", fieldValue)
                        }
                      })
                      checkForExistingPatient()
                    }}
                    onInputChange={(value: string, newValue: string) => {
                      if (
                        newValue === "" ||
                        validations.month.inputFormat.test(newValue)
                      ) {
                        if (newValue.length === 2) {
                          setValue("month", newValue)
                          dayRef && dayRef.current && dayRef.current.focus()
                        }
                        return newValue
                      }
                      return value
                    }}
                    onFocusChange={() => {
                      const fieldValue = watch("month")
                      setValue(
                        "month",
                        fieldValue[0] === "0" ? fieldValue[1] : fieldValue,
                      )
                    }}
                    errors={errors}
                    control={control}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormInput
                    formSxprops={{ my: 1 }}
                    inputRef={dayRef}
                    inputLabel={"formfields.day"}
                    placeholder={"formfields.dayplaceholder"}
                    fieldName={"day"}
                    rules={{
                      required: "formfieldErrors.dayrequired",
                      pattern: validations.day.pattern,
                      validate: (value) => {
                        return (
                          validateDate(value) || validations.day.pattern.message
                        )
                      },
                    }}
                    onBlurChange={() => {
                      trigger("day").then((valid) => {
                        if (valid) {
                          const fieldValue = watch("day")
                          fieldValue.length === 1
                            ? setValue("day", "0" + fieldValue)
                            : setValue("day", fieldValue)
                        }
                      })
                      checkForExistingPatient()
                    }}
                    onInputChange={(value: string, newValue: string) => {
                      if (
                        newValue === "" ||
                        validations.day.inputFormat.test(newValue)
                      ) {
                        if (newValue.length === 2) {
                          setValue("day", newValue)
                          yearRef.current && yearRef.current.focus()
                        }
                        return newValue
                      }
                      return value
                    }}
                    onFocusChange={() => {
                      const fieldValue = watch("day")
                      setValue(
                        "day",
                        fieldValue[0] === "0" ? fieldValue[1] : fieldValue,
                      )
                    }}
                    errors={errors}
                    control={control}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormInput
                    formSxprops={{ my: 1 }}
                    inputRef={yearRef}
                    inputLabel={"formfields.year"}
                    placeholder={"formfields.yearplaceholder"}
                    fieldName={"year"}
                    rules={{
                      required: "formfieldErrors.yearrequired",
                      pattern: validations.year.pattern,
                      validate: (value) =>
                        (Number(value) > 1900 &&
                          Number(value) <= new Date().getFullYear()) ||
                        "formfieldErrors.invalidYear",
                    }}
                    onBlurChange={() => {
                      trigger("year").then((valid) => {
                        if (valid) trigger("day")
                      })
                      checkForExistingPatient()
                    }}
                    onInputChange={(value: string, newValue: string) => {
                      return newValue === "" ||
                        validations.year.inputFormat.test(newValue)
                        ? newValue
                        : value
                    }}
                    errors={errors}
                    control={control}
                  />
                </Grid>

                <Grid item xs={12} sm={12}>
                  {existingPatientList.length !== 0 && (
                    <>
                      <Box sx={{ display: "flex", flexWrap: "wrap", ml: 0 }}>
                        <UText
                          variant={"caption"}
                          sxProp={{
                            mr: 0.5,
                            color: "#B3261E",
                            fontWeight: "500",
                            fontSize: "14px",
                          }}
                        >
                          {t("newpatient.existingPatientLabel")}
                        </UText>
                        {existingPatientList?.map((patient, index) => (
                          <Box
                            data-testid={`existingPatient-${patient?.id}`}
                            key={index}
                            sx={{ cursor: "pointer", display: "flex", mr: 0.5 }}
                            onClick={() => {
                              dispatch(setOpenExistingPatientCheckModal(true))
                              setExistingPatient(patient)
                            }}
                          >
                            <UText
                              variant={"caption"}
                              color={"primary.main"}
                              sxProp={{
                                textDecoration: "underline",
                                display: "flex",
                                fontWeight: "500",
                                fontSize: "14px",
                              }}
                            >
                              {`${patient.firstName} ${patient.lastName}`}
                            </UText>
                            {/* <img src={ExternalLink} alt="External Link"></img> */}
                            {index + 1 !== existingPatientList.length && (
                              <UText variant={"caption"}>{`,`}</UText>
                            )}
                          </Box>
                        ))}
                      </Box>
                      <UText variant={"caption"}>
                        {t("newpatient.existingPatientDesc")}
                      </UText>
                    </>
                  )}
                </Grid>
              </Grid>

              <UText variant={"h6"}>{t("formfields.assignto")}</UText>
              <Grid container rowSpacing={0} columnSpacing={1}>
                <Grid item xs={12}>
                  <FormAutoComplete
                    inputLabel={"formfields.alldoctors"}
                    fieldName={"doctor"}
                    rules={{
                      required: "formfieldErrors.assigntorequired",
                    }}
                    options={doctorList}
                    errors={errors}
                    control={control}
                    defaultValue={handleDefaultDoctorData()}
                  />
                </Grid>
              </Grid>
              <UText variant={"h6"}>{t("formfields.notes")}</UText>
              <Grid container rowSpacing={0} columnSpacing={1} marginBottom={2}>
                <Grid item xs={12}>
                  <FormInput
                    rules={{
                      maxLength: {
                        value: 255,
                        message: "formfieldErrors.noteserror",
                      },
                    }}
                    formSxprops={{ my: 0 }}
                    fieldName={"notes"}
                    errors={errors}
                    control={control}
                    multiline={true}
                    maxRows={4}
                  />
                  {!errors.notes && (
                    <UText
                      variant={"caption"}
                      color={"text.secondary"}
                      sxProp={{ ml: 2 }}
                    >
                      {t("formfields.characterLimit")}
                    </UText>
                  )}
                </Grid>
              </Grid>
              <Grid container justifyContent={"flex-end"}>
                <Grid item xs={12} sm={2.5}>
                  <UButton
                    sxProp={{ mt: 1, width: "100%", height: "36px" }}
                    variant={"contained"}
                    btnType={"submit"}
                    btnText={t("button.addPatient")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
      <ExistingPatientCheckModal
        isOpen={isExistingPatientCheckModal}
        patient={existingPatient}
        setClose={() => {
          dispatch(setOpenExistingPatientCheckModal(false))
          setExistingPatient(null)
        }}
        sxModalProps={{
          "#titleCntr": {
            padding: "16px 0px",
          },
          "#contentCntr": {
            padding: "20px 24px",
          },
          "#btnListCntr": {
            padding: "16px 0px",
            gap: 1,
          },
        }}
        createNewCase={checkforActiveCase}
      />
      <UModalPopup
        isAlert={
          isShowCancel &&
          Object.keys(getValues()).some((key) => !!getValues()[key]) &&
          preferences &&
          !preferences.on_show_new_patient_cancel_warning
        }
        title={
          <UText variant={"h6"}>{t("newpatient.cancelModal.title")} </UText>
        }
        content={<CancelModalContent />}
        sxModalProps={{
          "#titleCntr": {
            padding: "16px 24px",
          },
          "#contentCntr": {
            padding: "20px 24px",
          },
          "#btnListCntr": {
            padding: 1,
            gap: 1,
          },
        }}
        btnList={[
          <UButton
            key={t("newpatient.cancelModal.yes")}
            variant="contained"
            btnText={"Yes"}
            sxProp={{
              minWidth: 58,
              height: 36,
            }}
            onClickHandler={() => {
              setIsShowCancel(false)
              navigate("/patients")
            }}
          ></UButton>,
          <UButton
            key={t("newpatient.cancelModal.no")}
            variant="shade"
            btnText={"No"}
            sxProp={{
              minWidth: 54,
              height: 36,
              boxShadow:
                "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
            }}
            onClickHandler={() => {
              setIsShowCancel(false)
            }}
          ></UButton>,
        ]}
      ></UModalPopup>
      <NavigationBlockPopup
        contentText={t("newpatient.backbtnModal.text")}
        cancelNavigation={cancelNavigation}
        confirmNavigation={confirmNavigation}
        showPrompt={showPrompt}
      />
      {showScannerPatients && (
        <ScannerPatients
          showScannerPatients={showScannerPatients}
          setShowScannerPatients={setShowScannerPatients}
          choosePatient={choosePatient}
        />
      )}
      <CaseActionPopup
        isAlertOpen={isAlertOpen}
        setIsAlertOpen={setIsAlertOpen}
        caseId={caseId}
      />
    </>
  )
}

export default PatientForm
